'use client';

import CustomIconButton from "@/components/ui/CustomIconButton";
import FadeIn from "@/components/ui/FadeIn";
import { useWindowSize } from "@/hook/useWindowSize";
import { ProductsCarouselItemContent, ProductsCarouselItemContentProduct } from "@/types/sections.model";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Card, CardContent, CardMedia, Stack, Typography } from "@mui/material";
import Image from "next/image";
import React, { useRef } from "react";

import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation, Pagination } from "swiper/modules";


const ProductCarouselItem: React.FC<ProductsCarouselItemContentProduct> = ({ image, title }) => {

  return (
    <Card
      sx={{
        boxShadow: "none",
        position: "relative",
        backgroundColor: 'transparent',
      }}
    >
      {image && (
        <CardMedia
          title={title}
          sx={{
            borderRadius: 6,
            aspectRatio: '1',
            padding: 12,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }}>
          <Image
            src={image}
            alt={title}
            width={200}
            height={200}
            style={{ width: '100%', height: 'auto', borderRadius: 16, aspectRatio: '1', objectFit: 'contain' }}
          />
        </CardMedia>
      )}
      <CardContent>
        <Typography variant="bodyLGSemibold">{title}</Typography>
      </CardContent>
    </Card>
  )
}

const ProductsCarousel: React.FC<{ content: ProductsCarouselItemContent, items: ProductsCarouselItemContentProduct[] }> = ({ content, items }) => {

  const swiperRef = useRef(null);
  const dotsRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { isMobile } = useWindowSize();

  const slidesPerView = isMobile ? 1 : 4;
  const showNavigation = items.length > slidesPerView;

  if (!items || items.length === 0) return null;

  return (
    <Box py={{ xs: 32,lg: 48 }}>
      {items.length > 1 && (
        <Swiper
          ref={swiperRef}
          slidesPerView={slidesPerView}
          className={content.id + "-products-carousel"}
          modules={[Navigation, Pagination]}
          spaceBetween={16}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          navigation={{
            nextEl: nextRef.current,
            prevEl: prevRef.current,
          }}
          pagination={{
            clickable: true,
            el: dotsRef.current,
          }}
        >
          {items.map((item, index) => (
            <SwiperSlide key={item.id ?? `product-${item.title}-${index}`}>
              <ProductCarouselItem image={item.image} id={item.id} title={item.title} url={item.url} />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {items.length > 1 && (
        <FadeIn>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} mt={24}>
            <Box
              ref={dotsRef}
              sx={{
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start',
                },
                display: 'flex',
                gap: 1,
                '& .swiper-pagination-bullet': {
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme => theme.palette.primary.main,
                  opacity: 0.25,
                  transition: 'all 0.3s ease',
                },
                '& .swiper-pagination-bullet-active': {
                  opacity: 1,
                },
              }}
            />


            {!isMobile && showNavigation && (
              <Stack direction={"row"} spacing={8}>

                <CustomIconButton ref={prevRef} aria-label="Citazione precedente">
                  <KeyboardArrowLeftIcon />
                </CustomIconButton>

                <CustomIconButton ref={nextRef} aria-label="Citazione successiva">
                  <KeyboardArrowRightIcon />
                </CustomIconButton>

              </Stack>
            )}
          </Stack>
        </FadeIn>
      )}
    </Box>
  )
}

export default ProductsCarousel;
